import { DatePicker, Input, Switch } from "antd";
import { useTranslation } from "react-i18next";
import dayjs, { type Dayjs } from "dayjs";
import { formatCents, parseMoneyToCents, sanitizeMoneyInput } from "../../../../../utils/money";
import { GivenAmountField } from "../payment/GivenAmountField";
import { keypadTextOf, useKeypadOpen } from "../payment/keypadState";
import { MoneyKeypad } from "../payment/MoneyKeypad";
import { DepositFormField } from "./DepositFormField";
import { DepositTextField } from "./DepositTextField";

export interface DepositDetailsDraft {
  name: string;
  amountText: string;
  description: string;
  validUntil: Dayjs | null;
  withInvoice: boolean;
}

interface DepositDetailsColumnProps {
  draft: DepositDetailsDraft;
  onChange: (patch: Partial<DepositDetailsDraft>) => void;
  amountValid: boolean;
  /** „Фактуриране" се предлага само с правото „Фактуриране". */
  canInvoice: boolean;
  /** Защо фактура за депозита няма (фирмата е във „Фискално устройство") —
   *  „Фактуриране" стои сиво с тази причина; `null` — както преди. */
  invoiceBlockedReason: string | null;
  showErrors: boolean;
  onSubmit: () => void;
  isNarrow: boolean;
  /** Таблет с двете колони — по-дребните надписи (като в „Закрий"). */
  isCompact: boolean;
  isDesktop: boolean;
  /** Депозитът вече е записан (или може да е) — какво е взето не се сменя;
   *  „Фактуриране" остава (то е за след бона). */
  locked: boolean;
}

/**
 * Лявата колона на „Депозити" — какво се взема, със същите етикети и полета
 * като лявата колона на „Закрий": името е в кутията на полето за сума, сумата —
 * самото поле „Сума дадена от клиента" (EUR + екранната клавиатура, изборът
 * разпъната/свита е общ с „Закрий"), отдолу „За дата", описанието (1 ред,
 * расте до 3) и „Фактуриране" — след бона то отваря фактурата за депозита.
 * Методът е вдясно.
 */
export function DepositDetailsColumn({
  draft,
  onChange,
  amountValid,
  canInvoice,
  invoiceBlockedReason,
  showErrors,
  onSubmit,
  isNarrow,
  isCompact,
  isDesktop,
  locked,
}: DepositDetailsColumnProps) {
  const { t } = useTranslation();
  const keypad = useKeypadOpen();
  const amountCents = parseMoneyToCents(draft.amountText);

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <DepositFormField label={t("depositLoad.name")}>
        <DepositTextField
          value={draft.name}
          onChange={(name) => onChange({ name })}
          onEnter={onSubmit}
          placeholder={t("depositLoad.namePlaceholder")}
          maxLength={120}
          hasError={showErrors && !draft.name.trim()}
          isPhone={isNarrow}
          autoFocus={isDesktop}
          disabled={locked}
          leaveOnArrows
        />
      </DepositFormField>

      <div className="mt-5">
        <GivenAmountField
          label={t("depositLoad.amount")}
          value={draft.amountText}
          placeholder="0.00"
          onChange={(raw) => onChange({ amountText: sanitizeMoneyInput(raw) })}
          onNormalize={() => {
            if (draft.amountText !== "" && amountCents !== null) {
              onChange({ amountText: formatCents(amountCents) });
            }
          }}
          keypad={keypad}
          hasError={showErrors && !amountValid}
          isCompact={isCompact}
          isPhone={isNarrow}
          disabled={locked}
          leaveOnArrows
        />
      </div>
      {keypad.isOpen && !isNarrow && (
        <div className="mt-3">
          <MoneyKeypad
            onKey={(key) => onChange({ amountText: keypadTextOf(draft.amountText, key) })}
            disabled={locked}
          />
        </div>
      )}

      <div className="mt-5">
        <DepositFormField
          label={t("depositLoad.validUntil")}
          optionalLabel={t("invoiceCreate.optional")}
          hint={t("depositLoad.validUntilHint")}
        >
          <DatePicker
            value={draft.validUntil}
            onChange={(value) => onChange({ validUntil: value })}
            format="DD.MM.YYYY"
            placeholder={t("depositLoad.validUntilPlaceholder")}
            disabledDate={(day) => day.isBefore(dayjs().startOf("day"))}
            disabled={locked}
            className="w-full [&_input]:!font-semibold [&_input::placeholder]:!font-normal"
          />
        </DepositFormField>
      </div>

      <div className="mt-5">
        <DepositFormField label={t("depositLoad.description")} optionalLabel={t("invoiceCreate.optional")}>
          {/* Един ред колкото другите полета, расте до 3; над тях се
              превърта без видима лента. */}
          <Input.TextArea
            value={draft.description}
            maxLength={500}
            autoSize={{ minRows: 1, maxRows: 3 }}
            className="[scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            placeholder={t("depositLoad.descriptionPlaceholder")}
            disabled={locked}
            onChange={(event) => onChange({ description: event.target.value })}
          />
        </DepositFormField>
      </div>

      {canInvoice && (
        <div className="mt-5 rounded-xl border border-pos-line bg-pos-card">
          <label
            className={`flex items-center justify-between gap-3 px-4 py-3 ${
              invoiceBlockedReason ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <span className="flex min-w-0 flex-col">
              <span
                className={`font-bold ${invoiceBlockedReason ? "text-pos-muted" : "text-pos-ink"} ${
                  isCompact ? "text-[13px]" : "text-[14px]"
                }`}
              >
                {t("depositLoad.invoice")}
              </span>
              <span className="mt-0.5 text-[12.5px] font-medium text-pos-soft2">
                {invoiceBlockedReason ?? t("depositLoad.invoiceHint")}
              </span>
            </span>
            <Switch
              checked={draft.withInvoice && !invoiceBlockedReason}
              disabled={!!invoiceBlockedReason}
              onChange={(checked) => onChange({ withInvoice: checked })}
            />
          </label>
        </div>
      )}
    </div>
  );
}
